import { compilePossessor } from '../compile/possessor-compile';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers';
import { ecId, ecLean, itemField } from '../ability/context';
import { applyStatusRootFields, type StatusRootFields } from '../compile/status-root-residue';
import { storedStatusRows } from '../compile/creature-status-reader';
import { BURNABLE_LIGHT_IDS } from './pickup-light-ids';
import { applyResourceValues } from '../resources/resource-adjust';
import type { InventoryMoveReceipt } from '../../ft3d-move-handlers/inventory-move-receipt';

type Row = Record<string, unknown>;
const row = (value: unknown): Row => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : {};
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
export interface MemoryPickup {
    operationId: string; encounterId: string; actorId: string; destinationId: string;
    entries: { inventoryId: string; quantity: number; target: string | null; ap: number }[];
    combat: boolean; beforeAp: number | null;
}
export type MemoryPickupReceipt = InventoryMoveReceipt & { changed_paths: string[] };

/** Reuse equal held branches in the inventory compiler's owned output. Never copy the held member. */
export function retainHeld(previous: unknown, next: unknown): unknown {
    if (previous === next || !previous || !next || typeof previous !== 'object' || typeof next !== 'object'
        || Array.isArray(previous) !== Array.isArray(next)) return next;
    const prior = previous as Row, result = next as Row, keys = Object.keys(result);
    let equal = keys.length === Object.keys(prior).length;
    for (const key of keys) {
        result[key] = retainHeld(prior[key], result[key]);
        if (!Object.prototype.hasOwnProperty.call(prior, key) || result[key] !== prior[key]) equal = false;
    }
    return equal ? previous : next;
}

/** The ordinary move lane has already decided reach, capacity, stacking and AP.
 * Prepare the same possessor inventory clusters; commit only touched saved rows and surfaces.
 * No encounter/member compile, saved-world snapshot, or transport is involved. */
export function prepareMemoryPickup(base: Row, surface: Row, input: MemoryPickup): () => MemoryPickupReceipt[] {
    const fail = (reason: string): never => { throw new Error(`inventory-move:${reason}`); };
    const encounterId = upper(base.entity_id), actorId = upper(input.actorId);
    const members = Array.isArray(base.encounter_creatures_arr) ? base.encounter_creatures_arr as Row[] : [];
    const actor = members.find(member => upper(member.entity_id) === actorId);
    const displayed = (surface.encounter_creatures_arr as Row[]).find(member => upper(ecId(member)) === actorId);
    if (!actor || !displayed || upper(input.encounterId) !== encounterId) return fail('actor-not-held');
    if (upper(input.destinationId) !== actorId) return fail('container-destination');
    if (input.combat && Number(ecLean(displayed, 'aps') ?? 0) !== input.beforeAp) return fail('stale-ap');
    const catalog = row(base.catalog), receipts: MemoryPickupReceipt[] = [], writes: (() => void)[] = [];
    const heldComputed = row(displayed.computed);
    let inventory = displayed.inventory, catalogAfter = displayed.catalog;
    let creature = heldComputed.creature, vitality = heldComputed.vitality, ap = input.beforeAp;
    const rootScalars: Record<string, number | null> = {}, rootFields: StatusRootFields = {};
    const floor = readInventoryRows(base, 'inventory'), displayedFloor = readInventoryRows(surface, 'encounter_inventories_arr');
    const ids = new Set<string>();
    for (const entry of input.entries) {
        const id = upper(entry.inventoryId);
        if (ids.has(id)) return fail('duplicate-row'); ids.add(id);
        const own = readInventoryRows(actor, 'inventory').find(node => upper(readInventoryField(node, 'inventory_id')) === id);
        const saved = own ?? floor.find(node => upper(readInventoryField(node, 'inventory_id')) === id);
        const source = (own ? readInventoryRows(displayed, 'inventory') : displayedFloor).find(node => upper(readInventoryField(node, 'inventory_id')) === id);
        if (!saved || !source || upper(readInventoryField(saved, 'possess_id')) !== (own ? actorId : encounterId)) return fail('row-not-held');
        if (own && !readInventoryField(saved, 'equipped')) return fail('item-already-stowed');
        const statusIds = storedStatusRows(source, 'target').map(status => upper(itemField(status, 'status_item_id') ?? itemField(row(status.status_arr), 'item_id')));
        if (statusIds.includes('78C51D2D-5178-551C-1249-B5DDA4083098')) return fail('trapped-source');
        if (statusIds.some(id => BURNABLE_LIGHT_IDS.has(id))) return fail('light-timer');
        if (readInventoryField(saved, 'inventory_generation')) return fail('generated-source');
        const qty = Number(readInventoryField(saved, 'item_qty') ?? 1);
        if (qty !== entry.quantity) return fail('partial-stack');
        if (entry.target) return fail('stack-merge');
        // A batch's next cluster compile reads its preceding inventory result. Temporarily lend only those
        // four branch references to the synchronous engine, restoring them even if preparation refuses.
        const previous = { inventory: displayed.inventory, catalog: displayed.catalog, creature: heldComputed.creature, vitality: heldComputed.vitality };
        let compiled: ReturnType<typeof compilePossessor>;
        try {
            displayed.inventory = inventory; displayed.catalog = catalogAfter;
            heldComputed.creature = creature; heldComputed.vitality = vitality;
            compiled = compilePossessor(displayed, { kind: own ? 'unequip' : 'pickup', inventory_id: id, equipped: '' }, {
                catalog: row(displayed.catalog), transferCatalog: catalog, floorRows: [source], definition: id => catalog[id] ? row(catalog[id]) : null,
                conditionScene: () => ({ encounter: surface, campaign: surface.campaign_arr }),
                roster: () => surface.encounter_creatures_arr as Row[],
            });
        } finally {
            displayed.inventory = previous.inventory; displayed.catalog = previous.catalog;
            heldComputed.creature = previous.creature; heldComputed.vitality = previous.vitality;
        }
        if (!compiled.ok) return fail(compiled.reason);
        inventory = retainHeld(inventory, compiled.clusters.inventory); catalogAfter = retainHeld(catalogAfter, compiled.derived.catalog);
        creature = retainHeld(creature, compiled.derived['computed.creature']); vitality = retainHeld(vitality, compiled.derived['computed.vitality']);
        Object.assign(rootScalars, compiled.derived.root_scalars); Object.assign(rootFields, compiled.derived.root_fields);
        if (input.combat && ap !== null) ap -= entry.ap;
        const cols = { possess_id: actorId, equipped: '', inventory_wares: 0, x_pos: 0, y_pos: 0, z_pos: 0 };
        writes.push(() => {
            Object.assign(row(saved.inventory), cols);
            if (!own) {
                (base.inventory as Row[]).splice((base.inventory as Row[]).indexOf(saved), 1);
                (actor.inventory as Row[]).push(saved);
            }
            const order = (base.inventory_order as Row[]).find(node => upper(readInventoryField({ inventory: node }, 'inventory_id')) === id);
            if (order) {
                order.possess_id = actorId;
            }
            const at = (base.floor_order as string[]).indexOf(id);
            if (at >= 0) (base.floor_order as string[]).splice(at, 1);
        });
        receipts.push({ schema: 'ft3d.inventory-move-receipt.v1', ok: true, mutation_id: input.operationId, changed_paths: [],
            move: { outcome: 'full_move', moved_qty: qty,
                source: { inventory_id: id, possess_id_before: own ? actorId : encounterId, item_qty_after: qty, deleted_after: false },
                destination: { inventory_id: id, possess_id: actorId, item_qty_after: qty, created: false, equipped: '', position: { x: 0, y: 0, z: 0 } },
                roots: { source: own ? actorId : null, destination: actorId } },
            affected_caster_ids: [actorId], affected_encounter_ids: [encounterId], timing: { accepted_unix_ms: Date.now(), commit_ms: 0 } });
    }
    return () => {
        const paths = ['inventory'];
        for (const key of new Set([...Object.keys(row(heldComputed.creature)), ...Object.keys(row(creature))]))
            if (row(heldComputed.creature)[key] !== row(creature)[key]) paths.push(`computed.creature.${key}`);
        if (heldComputed.vitality !== vitality) paths.push('computed.vitality');
        if (input.combat) paths.push('encounter_creatures.aps', 'encounter_creatures.aps_used');
        for (const receipt of receipts) receipt.changed_paths = paths;
        for (const write of writes) write();
        // Scene perception and queued floor projections hold this installed list across envelope refreshes.
        const heldFloor = surface.encounter_inventories_arr as Row[];
        for (let at = heldFloor.length - 1; at >= 0; at--)
            if (ids.has(upper(readInventoryField(heldFloor[at]!, 'inventory_id')))) heldFloor.splice(at, 1);
        displayed.inventory = inventory; displayed.catalog = catalogAfter;
        heldComputed.creature = creature; heldComputed.vitality = vitality;
        for (const [key, value] of Object.entries(rootScalars)) { if (value === null) delete displayed[key]; else displayed[key] = value; }
        applyStatusRootFields(displayed, rootFields);
        const revision = Number(ecLean(displayed, 'movement_rev') ?? 0) + 1;
        for (const host of [actor, displayed]) {
            Object.assign(row(host.encounter_creatures), { movement_rev: revision, ...(input.combat ? { aps: ap } : {}) });
            host.base_rev = Number(host.base_rev ?? 0) + 1;
        }
        if (input.combat && ap !== null) applyResourceValues(displayed, { aps: ap }, { layer: () => null, hasLayer: () => false });
        return receipts;
    };
}
